import type { MaterialSupplierForm } from "@/api/materialSupplierService";
import {
  DefaultPrimaryButton,
  DefaultSecondaryButton,
  InformationModal,
} from "@/styles/globalstyles";
import { Modal, Box, Typography, Divider, Button, Switch } from "@mui/material";
import CloseButton from "../buttons/CloseButton";
import { useEffect, useState } from "react";
import {
  useCreateMaterialSupplier,
  useGetFormulars,
  useGetMaterialSupplierById,
  useUpdateMaterialSupplier,
} from "@/hooks/materialSupplierHook";
import type { Formular } from "@/api/materialSupplierService";
import type { MaterialType } from "@/api/materialTypeService";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { getAxiosErrorMessage } from "@/utils/healper";
import BasicInfoFields from "./BasicInfoFields";
import PriceCalculationFields from "./PriceCalculationFields";
import MaterialTypeSelection from "./MaterialTypeSelection";

interface SaveMaterialSupplierModalProps {
  open: boolean;
  onClose: () => void;
  materialSupplierId?: number;
  matType: MaterialType[];
}

const SaveMaterialSupplierModal = ({
  open,
  onClose,
  materialSupplierId,
  matType,
}: SaveMaterialSupplierModalProps) => {
  const [formData, setFormData] = useState<MaterialSupplierForm>({
    name: "",
    name_abbr: "",
    address_text: "",
    email: "",
    phones: [],
    is_active: true,
    attributes: {
      commission_pct: 0,
      stock_pct: 0,
      credit_pct: 0,
      rounding: "ROUND",
      rounding_digits: 0,
      usance: 0,
      interest: 0,
    },
    material_type_ids: [],
    formula_id: 0,
  });
  const [phoneInput, setPhoneInput] = useState("");
  const [formular, setFormular] = useState<Formular | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: formularData, isLoading: isLoadingFormulars } =
    useGetFormulars();

  const {
    data: materialSupplierData,
    isLoading: isLoadingMaterialSupplier,
    isError: isErrorMaterialSupplier,
  } = useGetMaterialSupplierById(materialSupplierId);

  const createMutation = useCreateMaterialSupplier();
  const updateMutation = useUpdateMaterialSupplier(materialSupplierId || 0);

  useEffect(() => {
    if (formData && formData.formula_id && formularData) {
      setFormular(
        formularData.find((f) => f.formula_id === formData.formula_id) || null
      );
    }
  }, [formData, formularData]);

  useQueryHandler({
    isError: isErrorMaterialSupplier,
    isFetching: isLoadingMaterialSupplier,
  });

  useQueryHandler({
    isError: createMutation.isError,
    isFetching: createMutation.isPending,
    isSuccess: createMutation.isSuccess,
    successMessage: "เพิ่ม Material Supplier สำเร็จ",
    error: createMutation.error,
    errorMessage: getAxiosErrorMessage(createMutation.error),
  });

  useQueryHandler({
    isError: updateMutation.isError,
    isFetching: updateMutation.isPending,
    isSuccess: updateMutation.isSuccess,
    successMessage: "แก้ไข Material Supplier สำเร็จ",
    error: updateMutation.error,
    errorMessage: getAxiosErrorMessage(updateMutation.error),
  });

  useEffect(() => {
    if (open && formularData) {
      setFormData({
        name: materialSupplierData?.name || "",
        name_abbr: materialSupplierData?.name_abbr || "",
        address_text: materialSupplierData?.address_text || "",
        email: materialSupplierData?.email || "",
        phones: materialSupplierData?.phones || [],
        is_active: materialSupplierData?.is_active ?? true,
        attributes: materialSupplierData?.attributes || {
          commission_pct: 0,
          stock_pct: 0,
          credit_pct: 0,
          rounding: "ROUND",
          rounding_digits: 0,
          usance: 0,
          interest: 0,
        },
        material_type_ids:
          materialSupplierData?.material_type_ids.map((mt) => mt.id) || [],
        formula_id:
          materialSupplierData?.formula?.formula_id ||
          formularData?.find((item) => item.formula_code === "OTHER")
            ?.formula_id ||
          0,
      });
    }
  }, [materialSupplierId, materialSupplierData, open, formularData]);

  const handleClose = () => {
    setPhoneInput("");
    setFormular(null);
    setFormData({
      name: "",
      name_abbr: "",
      address_text: "",
      email: "",
      phones: [],
      is_active: true,
      attributes: {
        commission_pct: 0,
        stock_pct: 0,
        credit_pct: 0,
        rounding: undefined,
        rounding_digits: 0,
        usance: 0,
        interest: 0,
      },
      material_type_ids: [],
      formula_id: 0,
    });
    onClose();
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name) {
      newErrors.name = "กรุณากรอกชื่อ";
    }

    if (!formData.name_abbr) {
      newErrors.name_abbr = "กรุณากรอก Supplier Code";
    }

    if (!formData.formula_id) {
      newErrors.formula_id = "กรุณาเลือก Formula";
    }

    if (formData.attributes.commission_pct === undefined) {
      newErrors.commission_pct = "กรุณากรอก Commission";
    }

    if (
      formular?.has_stock_pct &&
      formData.attributes.stock_pct === undefined
    ) {
      newErrors.stock_pct = "กรุณากรอก Stock";
    }

    if (
      formular?.has_credit_pct &&
      formData.attributes.credit_pct === undefined
    ) {
      newErrors.credit_pct = "กรุณากรอก Credit";
    }

    if (formular?.has_rounding && formData.attributes.rounding === undefined) {
      newErrors.rounding = "กรุณาเลือก Rounding";
    }

    if (
      formular?.has_rounding_digits &&
      formData.attributes.rounding_digits === undefined
    ) {
      newErrors.rounding_digits = "กรุณากรอก Rounding Digits";
    }

    if (formular?.has_usance && formData.attributes.usance === undefined) {
      newErrors.usance = "กรุณากรอก Usance";
    }

    if (formular?.has_interest && formData.attributes.interest === undefined) {
      newErrors.interest = "กรุณากรอก Interest";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAdd = () => {
    if (!validateForm()) return;
    createMutation.mutate(formData);
    handleClose();
  };

  const handleEdit = () => {
    if (!validateForm()) return;
    updateMutation.mutate(formData);
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={{ ...InformationModal, maxHeight: "90vh", width: "80vw" }}>
        <CloseButton onClick={handleClose} />
        <Typography variant="h2">
          {materialSupplierId
            ? "แก้ไข Material Supplier"
            : "เพิ่ม Material Supplier"}
        </Typography>
        <Box p={1}>
          <Divider sx={{ my: 1 }} />
          <Box mt={1} display="flex" flexDirection="column" gap={1.5}>
            <BasicInfoFields
              formData={formData}
              setFormData={setFormData}
              phoneInput={phoneInput}
              setPhoneInput={setPhoneInput}
              errors={errors}
              setErrors={setErrors}
              materialSupplierId={materialSupplierId}
            />
            <PriceCalculationFields
              formData={formData}
              setFormData={setFormData}
              formular={formular}
              formularData={formularData}
              isLoadingFormulars={isLoadingFormulars}
              errors={errors}
              setErrors={setErrors}
              materialSupplierId={materialSupplierId}
            />

            <MaterialTypeSelection
              formData={formData}
              setFormData={setFormData}
              matType={matType}
              materialSupplierId={materialSupplierId}
              materialSupplierData={materialSupplierData}
            />
          </Box>
          <Box mt={3} display="flex" justifyContent="space-between">
            {materialSupplierId != undefined ? (
              <Box display="flex" alignItems="center">
                <Switch
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                />
                <Typography fontWeight="bold">Active</Typography>
              </Box>
            ) : (<Box></Box>)}
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                sx={DefaultSecondaryButton}
                onClick={handleClose}
              >
                ยกเลิก
              </Button>
              <Button
                variant="contained"
                sx={DefaultPrimaryButton}
                onClick={materialSupplierId ? handleEdit : handleAdd}
              >
                บันทึก
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default SaveMaterialSupplierModal;
